/**
 * Extrae año y prefijo de etiquetas tipo SS2027, AW26, PF2028.
 */
export function parseTemporadaSortKey(label) {
  const raw = String(label || "")
    .trim()
    .toUpperCase();
  const match = raw.match(/^(.*?)(\d{2}|\d{4})\s*$/);
  if (!match) {
    return { year: 0, prefix: raw };
  }
  let year = Number(match[2]);
  if (match[2].length === 2) {
    year += year >= 70 ? 1900 : 2000;
  }
  const prefix = String(match[1] || "")
    .replace(/[^A-Z]/g, "")
    .trim();
  return { year, prefix: prefix || raw };
}

/**
 * Año más reciente primero; dentro del mismo año, alfabético (AW, FA, PF…).
 */
export function compareTemporadasByYearThenAlpha(a, b) {
  const labelA = typeof a === "string" ? a : a?.TEMPORADA || a?.Temporada || "";
  const labelB = typeof b === "string" ? b : b?.TEMPORADA || b?.Temporada || "";
  const ka = parseTemporadaSortKey(labelA);
  const kb = parseTemporadaSortKey(labelB);
  if (ka.year !== kb.year) return kb.year - ka.year;
  const byPrefix = ka.prefix.localeCompare(kb.prefix, "es");
  if (byPrefix !== 0) return byPrefix;
  return String(labelA).localeCompare(String(labelB), "es");
}

export function sortTemporadasByYearThenAlpha(list) {
  return [...(Array.isArray(list) ? list : [])].sort(
    compareTemporadasByYearThenAlpha,
  );
}

/**
 * Deja temporadas iguales o más nuevas que minLabel (p. ej. AW2025).
 * Orden del catálogo: año reciente primero; en el mismo año, prefijo A→Z.
 */
export function filterTemporadasFromLabel(list, minLabel = "AW2025") {
  const min = String(minLabel || "").trim();
  if (!min) return [...(Array.isArray(list) ? list : [])];
  return (Array.isArray(list) ? list : []).filter((row) => {
    const label = typeof row === "string" ? row : row?.TEMPORADA || "";
    if (!String(label).trim()) return false;
    return compareTemporadasByYearThenAlpha(label, min) <= 0;
  });
}

/** Filtra desde minLabel y ordena de actual a más vieja. */
export function sortTemporadasFromLabel(list, minLabel = "AW2025") {
  return sortTemporadasByYearThenAlpha(
    filterTemporadasFromLabel(list, minLabel),
  );
}

/** FW en el título de la reunión es AW en el catálogo. */
const TEMPORADA_PREFIX_ALIAS = {
  FW: "AW",
  AW: "FW",
};

function seasonTokensFromTitle(title) {
  const raw = String(title || "").toUpperCase();
  const re = /\b([A-Z](?:\/[A-Z])?|[A-Z]{2,4})[\s./-]*(\d{2}|\d{4})\b/g;
  const tokens = [];
  let match;
  while ((match = re.exec(raw))) {
    let year = Number(match[2]);
    if (match[2].length === 2) year += year >= 70 ? 1900 : 2000;
    const prefix = String(match[1] || "").replace(/[^A-Z]/g, "");
    if (prefix && year) tokens.push({ prefix, year });
  }
  return tokens;
}

function temporadaRowKeys(row) {
  return [row?.TEMPORADA, row?.ABREVIATURA]
    .map((value) => parseTemporadaSortKey(value))
    .filter((key) => key.year > 0 && key.prefix);
}

function rowMatchesToken(row, token, { alias = false } = {}) {
  return temporadaRowKeys(row).some((key) => {
    if (key.year !== token.year) return false;
    if (key.prefix === token.prefix) return !alias;
    return alias && TEMPORADA_PREFIX_ALIAS[token.prefix] === key.prefix;
  });
}

/**
 * Temporada del catálogo escrita en el título (FW27 → AW2027).
 * Devuelve el TEMPORADA del desplegable, o "".
 */
export function temporadaFromMeetingTitle(title, temporadas) {
  const tokens = seasonTokensFromTitle(title);
  const rows = (Array.isArray(temporadas) ? temporadas : []).filter((row) =>
    String(row?.TEMPORADA || "").trim(),
  );
  if (!tokens.length || !rows.length) return "";
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const exact = rows.find((row) => rowMatchesToken(row, tokens[i]));
    if (exact) return String(exact.TEMPORADA).trim();
  }
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const aliased = rows.find((row) =>
      rowMatchesToken(row, tokens[i], { alias: true }),
    );
    if (aliased) return String(aliased.TEMPORADA).trim();
  }
  return "";
}
